<template>
  <section class="ngo-infographic" aria-labelledby="ngo-snapshot-title" :aria-busy="loading">
    <div class="card-kicker">The registry at a glance</div>
    <header class="snapshot-header">
      <div>
        <h3 id="ngo-snapshot-title">NGOs in focus</h3>
        <span v-if="demo" class="demo-label">Demo · Sample data</span>
      </div>
      <span class="snapshot-icon" aria-hidden="true"
        ><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M4 20V10h4v10m2 0V4h4v16m2 0v-7h4v7M2 20h20" /></svg
      ></span>
    </header>

    <div v-if="loading" class="snapshot-message" role="status">Loading NGO insights…</div>
    <div v-else-if="error" class="snapshot-message" role="status">
      <p>NGO insights are unavailable right now.</p>
      <button type="button" class="retry" @click="loadOrganizations">Try again</button>
    </div>
    <div v-else-if="!organizations.length" class="snapshot-message">
      No NGO records available yet.
    </div>
    <template v-else>
      <div class="snapshot-summary">
        <div class="donut" :style="donutStyle" role="img" :aria-label="chartDescription">
          <div class="donut-center">
            <strong>{{
              selected ? selected.count.toLocaleString() : total.toLocaleString()
            }}</strong
            ><span>{{
              selected ? 'in selection' : demo ? 'sample NGOs' : 'NGOs in registry'
            }}</span>
          </div>
        </div>
        <div class="summary-copy">
          <span class="summary-kicker">{{
            selected ? 'Selected insight' : 'Connected by purpose'
          }}</span
          ><strong>{{ selected ? selected.name : 'A growing community of health NGOs' }}</strong>
          <p>
            {{
              selected
                ? `${selectedShare}% of ${demo ? 'sample' : 'loaded'} NGO records`
                : 'Explore the people and organizations behind the numbers.'
            }}
          </p>
          <button v-if="selected" type="button" class="reset" @click="selectedName = null">
            Show all NGOs ↗
          </button>
        </div>
      </div>

      <div class="breakdown-controls" role="group" aria-label="Group NGOs by">
        <button
          v-for="option in dimensions"
          :key="option.key"
          type="button"
          :aria-pressed="dimension === option.key"
          @click="changeDimension(option.key)"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="breakdown" aria-live="polite">
        <button
          v-for="group in visibleGroups"
          :key="group.name"
          type="button"
          class="breakdown-row"
          :aria-pressed="selectedName === group.name"
          @click="selectedName = selectedName === group.name ? null : group.name"
        >
          <span class="row-label"
            ><span>{{ group.name }}</span
            ><strong>{{ group.count.toLocaleString() }}</strong></span
          >
          <span class="bar-track" aria-hidden="true"
            ><span
              :style="{ width: `${(group.count / largestGroup) * 100}%`, background: group.color }"
            ></span
          ></span>
        </button>
      </div>
      <p class="chart-note">
        {{ groups.length > 4 ? 'Top 4 shown. ' : '' }}Select a bar to explore its share.<template
          v-if="total > organizations.length"
        >
          Breakdown based on {{ organizations.length.toLocaleString() }} loaded records of
          {{ total.toLocaleString() }}.</template
        >
      </p>
    </template>
    <router-link :to="{ name: 'registered ngos' }" class="registry-link"
      >Explore the NGO registry <span aria-hidden="true">↗</span></router-link
    >
  </section>
</template>

<script>
import { getOrganizations } from '../../config/api-modules/api.organization';
import sampleData from '../../config/ngo-infographic-sample.json';

const COLORS = ['#b6ce81', '#71996b', '#3d7459', '#d7b975', '#819b9a'];

export default {
  name: 'NgoInfographic',
  props: {
    demo: { type: Boolean, default: false },
  },
  data() {
    return {
      organizations: [],
      total: 0,
      loading: true,
      error: false,
      dimension: 'type',
      selectedName: null,
      dimensions: [
        { key: 'type', label: 'By organization type' },
        { key: 'state', label: 'By state' },
      ],
    };
  },
  computed: {
    groups() {
      const counts = Object.create(null);
      this.organizations.forEach((organization) => {
        const raw = organization[this.dimension];
        const name = (typeof raw === 'string' ? raw.trim() : '') || 'Not specified';
        counts[name] = (counts[name] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .map((group, index) => ({ ...group, color: COLORS[Math.min(index, 4)] }));
    },
    visibleGroups() {
      return this.groups.slice(0, 4);
    },
    largestGroup() {
      return this.groups[0]?.count || 1;
    },
    selected() {
      return this.groups.find((group) => group.name === this.selectedName);
    },
    selectedShare() {
      return this.selected
        ? Math.round((this.selected.count / this.organizations.length) * 100)
        : 0;
    },
    chartDescription() {
      return this.selected
        ? `${this.selected.name}: ${this.selected.count} NGOs, ${this.selectedShare}% of ${
            this.demo ? 'sample' : 'loaded'
          } records`
        : `${this.total} ${
            this.demo ? 'sample NGOs' : 'NGOs in registry'
          }. Chart shows the distribution of ${this.organizations.length} ${
            this.demo ? 'sample' : 'loaded'
          } records by ${this.dimension}.`;
    },
    donutStyle() {
      if (!this.organizations.length) return {};
      if (this.selected) {
        const share = (this.selected.count / this.organizations.length) * 100;
        return {
          background: `conic-gradient(${this.selected.color} 0% ${share}%, #345445 ${share}% 100%)`,
        };
      }
      let position = 0;
      const stops = this.groups.map((group) => {
        const start = position;
        position += (group.count / this.organizations.length) * 100;
        return `${group.color} ${start}% ${position}%`;
      });
      return { background: `conic-gradient(${stops.join(', ')})` };
    },
  },
  mounted() {
    this.loadOrganizations();
  },
  methods: {
    changeDimension(dimension) {
      this.dimension = dimension;
      this.selectedName = null;
    },
    async loadOrganizations() {
      if (this.demo) {
        const types = sampleData.organizationTypes.flatMap(({ name, count }) =>
          Array(count).fill(name)
        );
        const states = sampleData.states.flatMap(({ name, count }) => Array(count).fill(name));
        this.organizations = types.map((type, index) => ({ type, state: states[index] }));
        this.total = this.organizations.length;
        this.error = false;
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        const { data } = await getOrganizations();
        const records = Array.isArray(data) ? data : data?.results;
        if (!Array.isArray(records)) throw new Error('Invalid registry response');
        this.organizations = records.filter((record) => record && typeof record === 'object');
        this.total =
          Number.isInteger(data?.count) && data.count >= this.organizations.length
            ? data.count
            : this.organizations.length;
      } catch (error) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ngo-infographic {
  min-width: 0;
  padding: clamp(1.25rem, 2.5vw, 2rem);
  border: 1px solid #365b44;
  border-radius: 24px;
  color: #f3f6ec;
  background: radial-gradient(ellipse at top right, #335b42, transparent 65%), #193b2b;
  box-shadow: 0 18px 35px rgba(18, 51, 30, 0.15);
  align-self: center;
}
.snapshot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.card-kicker {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #486048;
  color: #bfd0ad;
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
}
.snapshot-header h3 {
  margin: 8px 0 0;
  font-size: 1.4rem;
  color: #f3f6ec;
  font-weight: 600;
}
.demo-label {
  display: inline-block;
  margin-top: 10px;
  padding: 4px 8px;
  border: 1px solid #68805a;
  border-radius: 6px;
  color: #d3e8ad;
  font-size: 0.62rem;
}
.snapshot-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 1px solid #56735a;
  border-radius: 12px;
  color: #c4d995;
  flex-shrink: 0;
}
.snapshot-icon svg {
  width: 23px;
  height: 23px;
}
.snapshot-summary {
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin: 1.8rem 0;
}
.donut {
  display: grid;
  place-items: center;
  width: 156px;
  height: 156px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 7px rgba(213, 230, 180, 0.04);
}
.donut-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 126px;
  height: 126px;
  border-radius: 50%;
  background: #1d3e2e;
}
.donut-center strong {
  font-size: 1.9rem;
  line-height: 1.2;
  letter-spacing: -0.04em;
}
.donut-center span {
  margin-top: 6px;
  font-size: 0.6rem;
  color: #c3d1b9;
}
.summary-kicker {
  color: #c2d898;
  font-size: 0.59rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.summary-copy {
  min-width: 0;
}
.summary-copy strong {
  display: block;
  margin: 7px 0;
  font-size: 0.93rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.summary-copy p {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.65;
  color: #c3d1b9;
}
.breakdown-controls {
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid #45604b;
  border-radius: 10px;
  background: #163424;
}
.breakdown-controls button {
  flex: 1;
  padding: 9px 6px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #c3d1b9;
  font-size: 0.68rem;
  font-weight: 600;
}
.breakdown-controls button[aria-pressed='true'] {
  color: #1c3a2a;
  background: #deebc9;
}
.breakdown {
  display: grid;
  gap: 4px;
  margin-top: 14px;
}
.breakdown-row {
  width: 100%;
  padding: 8px;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #edf3e5;
  background: transparent;
}
.breakdown-row:hover,
.breakdown-row[aria-pressed='true'] {
  background: #2e503a;
  border-color: #68805a;
}
.row-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 7px;
  font-size: 0.7rem;
}
.row-label > span {
  overflow-wrap: anywhere;
}
.bar-track {
  display: block;
  height: 6px;
  border-radius: 4px;
  background: #345445;
  overflow: hidden;
}
.bar-track > span {
  display: block;
  height: 100%;
  border-radius: inherit;
}
.chart-note {
  margin: 12px 0 0;
  color: #bdcdb4;
  font-size: 0.63rem;
  line-height: 1.6;
}
.registry-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #486048;
  color: #d3e8ad;
  font-size: 0.76rem;
  font-weight: 600;
}
.registry-link:hover {
  color: #fff;
}
.snapshot-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 310px;
  text-align: center;
  color: #d3dfca;
  font-size: 0.9rem;
}
.retry,
.reset {
  border: 0;
  background: transparent;
  color: #d3e8ad;
  text-decoration: underline;
  font-size: 0.72rem;
  padding: 8px 0;
}
button:focus-visible,
a:focus-visible {
  outline: 2px solid #d7eaa6;
  outline-offset: 3px;
}
@media (max-width: 420px) {
  .snapshot-summary {
    flex-direction: column;
    text-align: center;
  }
  .summary-copy {
    max-width: 240px;
  }
}
</style>
